/**
 * Audit chain integrity — the operator diagnosis panel (ENG-1344,
 * production bug TID-2026-07-28-01).
 *
 * A concurrent append on a second replica broke an evidentiary audit chain.
 * Re-baselining (sealing the broken chain and starting a fresh segment) is
 * the correct remedy for that benign race and the WRONG remedy for edited or
 * deleted records, because it archives the evidence and moves on. The
 * backend now classifies every break (AnalyzeEvidentiaryChain) and refuses to
 * seal a tampered chain (EvaluateRebaseline). This page is the surface a
 * human looks at BEFORE deciding, so the three facts that decide it have to
 * be visible:
 *
 *  1. WHERE and WHEN — every anomaly renders its index, record id, an
 *     explicit UTC timestamp derived from atUnixNano, its event code, both
 *     hash pairs, and its explanation. A diagnosis without *when* cannot be
 *     correlated with a deploy or an incident window, which is most of what
 *     an operator is actually trying to do here.
 *  2. TAMPERING BLOCKS RE-BASELINE — the control is `disabled` and a
 *     prominent warning is shown. If it rendered enabled, a real attack
 *     could be sealed away by an operator who believed they were clearing a
 *     benign race. That is the exact failure this whole feature exists to
 *     prevent.
 *  3. TRUNCATION IS A DIFFERENT FACT — `truncated` means "I could not see
 *     all of the chain", not "what I saw was altered". It gets its own
 *     alert, and a truncated-but-clean diagnosis is NEVER rendered as
 *     "chain is intact". Reassuring on a partial view is precisely the
 *     false negative the backend fix just closed.
 *
 * Re-baseline is blocked while truncated as well as while tampering is
 * suspected. That is deliberately stricter than EvaluateRebaseline, which
 * only reads TamperSuspected: sealing on a partial view would archive a
 * segment that was never examined, which is the same reasoning
 * ErrRebaselineNoChain uses to refuse a zero-record diagnosis outright. The
 * UI is defense in depth, never the enforcement point — the server decides.
 *
 * PRIVACY: this page renders only what GET
 * /v1/admin/tenants/{tenantId}/audit/chain-integrity returns. The API does
 * not return record subjects or JTIs and nothing here adds them, and the
 * hash fields are rendered exactly as received — never rehydrated, extended,
 * or logged.
 */
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import type {
  AdminGetAuditChainIntegrityResp,
  ChainAnomaly,
} from '@gen/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

const Alert = styled.div<{ $tone: 'danger' | 'warning' | 'ok' }>`
  margin: 0;
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  border-radius: 6px;
  border-left: 6px solid
    ${({ theme, $tone }) => {
    const u = asUnified(theme)
    if ($tone === 'danger') return u.colors?.danger ?? '#991b1b'
    if ($tone === 'warning') return u.colors?.warning ?? '#b45309'
    return u.colors?.success ?? '#047857'
  }};
  background: ${({ theme }) => asUnified(theme).bg?.surface ?? '#f5f5f5'};
  color: ${({ theme }) => asUnified(theme).text.primary};
  font-weight: ${({ $tone }) => ($tone === 'ok' ? 400 : 600)};
`

const AnomalyCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  border: 1px solid ${({ theme }) => asUnified(theme).border?.color ?? '#ddd'};
  border-radius: 6px;
`

const AnomalyHeading = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.md};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  align-items: baseline;
`

const FieldLabel = styled.span`
  min-width: 12rem;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

const Mono = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-all;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const RebaselineButton = styled.button`
  align-self: flex-start;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Formats atUnixNano as an explicit UTC ISO-8601 instant.
 *
 * Deliberately NOT a locale-formatted date: an operator correlating a chain
 * break against a deploy window or an incident timeline needs an unambiguous
 * instant, and a locale string silently differs per browser and per runner.
 * Millisecond truncation is lossless enough for that purpose (a JS number
 * cannot hold nanosecond precision at 2025-era epochs anyway, so pretending
 * otherwise would be a lie), and `null` is returned rather than guessing when
 * the field is absent.
 */
export const formatAtUnixNano = (atUnixNano?: number): string | null => {
  if (atUnixNano === undefined || atUnixNano === null) return null
  if (!Number.isFinite(atUnixNano)) return null
  const ms = Math.floor(atUnixNano / 1e6)
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

/**
 * Plain-language gloss for the analyzer's `kind`. The raw wire value is
 * ALWAYS rendered alongside this — an operator pasting a classification into
 * an incident channel needs the exact token the backend emitted, and a label
 * that quietly drifts from the analyzer would be worse than no label.
 */
export const kindLabel = (kind?: string): string => {
  switch (kind) {
    case 'reorder_race':
      return 'Concurrent append (benign reorder)'
    case 'content_tampered':
      return 'Content altered after the fact'
    case 'missing_record':
      return 'Record missing from the chain'
    case 'unknown_linkage':
      return 'Linkage could not be explained'
    default:
      return 'Unclassified'
  }
}

// ---------------------------------------------------------------------------
// AuditChainIntegrity — presentational panel (testable without a router)
// ---------------------------------------------------------------------------

export interface AuditChainIntegrityProps {
  diagnosis: AdminGetAuditChainIntegrityResp
  /**
   * Invoked when the operator confirms a re-baseline. Optional because no
   * re-baseline endpoint is exposed yet (the gate itself lives server-side in
   * EvaluateRebaseline); the control's DISABLED state is the property this
   * panel is responsible for, and it holds whether or not a handler is wired.
   */
  onRebaseline?: () => void
}

const AnomalyDetail = ({ anomaly }: { anomaly: ChainAnomaly }): JSX.Element => {
  const at = formatAtUnixNano(anomaly.atUnixNano)
  const chainedFrom =
    anomaly.matchesIndex !== undefined && anomaly.matchesIndex >= 0
      ? anomaly.matchesIndex
      : null

  return (
    <AnomalyCard>
      <AnomalyHeading>
        Record {anomaly.index ?? '?'} — {kindLabel(anomaly.kind)}
      </AnomalyHeading>

      <FieldRow>
        <FieldLabel>Classification</FieldLabel>
        <Mono>{anomaly.kind ?? 'unknown'}</Mono>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Record id</FieldLabel>
        <Mono>{anomaly.recordId ?? '(not reported)'}</Mono>
      </FieldRow>

      <FieldRow>
        <FieldLabel>When (UTC)</FieldLabel>
        {at ? (
          <time dateTime={at}>{at}</time>
        ) : (
          <MutedText>Timestamp not reported</MutedText>
        )}
      </FieldRow>

      <FieldRow>
        <FieldLabel>Event</FieldLabel>
        <Mono>{anomaly.eventCode ?? '(not reported)'}</Mono>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Stored previous hash</FieldLabel>
        <Mono>{anomaly.storedPrevHash ?? '(none)'}</Mono>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Expected previous hash</FieldLabel>
        <Mono>{anomaly.expectedPrevHash ?? '(none)'}</Mono>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Stored record hash</FieldLabel>
        <Mono>{anomaly.storedRecordHash ?? '(none)'}</Mono>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Computed record hash</FieldLabel>
        <Mono>{anomaly.computedRecordHash ?? '(none)'}</Mono>
      </FieldRow>

      {chainedFrom !== null && (
        <MutedText>
          This write chained from record {chainedFrom}, not from record{' '}
          {anomaly.index !== undefined ? anomaly.index - 1 : '?'}.
        </MutedText>
      )}

      {anomaly.explanation && <MutedText>{anomaly.explanation}</MutedText>}
    </AnomalyCard>
  )
}

export const AuditChainIntegrity = ({
  diagnosis,
  onRebaseline,
}: AuditChainIntegrityProps): JSX.Element => {
  const anomalies = diagnosis.anomalies ?? []
  const total = diagnosis.total ?? 0
  const tamperSuspected = diagnosis.tamperSuspected === true
  const truncated = diagnosis.truncated === true

  // Fail closed. Tampering is the safety property this feature exists for;
  // truncation is blocked for a different reason — a partial view cannot
  // support sealing the part it never looked at.
  const blocked = tamperSuspected || truncated

  const blockedReason = (): string => {
    if (tamperSuspected) {
      return 'Re-baseline is blocked: tampering is suspected.'
    }
    if (truncated) {
      return 'Re-baseline is blocked: the diagnosis is incomplete.'
    }
    return 'Re-baseline seals the current chain and starts a new segment.'
  }

  return (
    <PageContainer>
      <PageHeading>Audit chain integrity</PageHeading>

      <MutedText>
        {total} records examined; {anomalies.length} anomalies classified.
      </MutedText>

      {tamperSuspected && (
        <Alert role="alert" $tone="danger">
          Tampering suspected. At least one break is not a concurrent-append
          race, so re-baselining would seal and archive evidence of a possible
          attack. Investigate before taking any action that alters the chain.
        </Alert>
      )}

      {truncated && (
        <Alert role="alert" $tone="warning">
          Only part of the chain was examined. Records outside the examined
          window were not checked at all, so this diagnosis cannot account for
          them — a clean result here is not a clean bill of health for the
          whole chain. Narrow the range or re-run against the full chain.
        </Alert>
      )}

      {!tamperSuspected && !truncated && anomalies.length === 0 && (
        <Alert role="status" $tone="ok">
          Chain is intact — all {total} examined records link correctly.
        </Alert>
      )}

      {anomalies.map((a) => (
        <AnomalyDetail
          key={a.recordId ?? `index-${String(a.index)}`}
          anomaly={a}
        />
      ))}

      <MutedText>{blockedReason()}</MutedText>

      <RebaselineButton
        type="button"
        disabled={blocked}
        onClick={onRebaseline}
      >
        Re-baseline chain
      </RebaselineButton>
    </PageContainer>
  )
}

// ---------------------------------------------------------------------------
// AuditChainIntegrityPage — route-connected wrapper
// ---------------------------------------------------------------------------

export const AuditChainIntegrityPage = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [diagnosis, setDiagnosis] =
    useState<AdminGetAuditChainIntegrityResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (!tenant || tenant === 'system') {
      setDiagnosis(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const resp = await client.adminGetAuditChainIntegrity({
        tenantId: tenant,
      })
      setDiagnosis(resp)
    } catch (e: unknown) {
      // Never fall through to an empty-looking panel: "the request failed" and
      // "the chain is clean" must not look the same to an operator.
      setDiagnosis(null)
      setError(e instanceof Error ? e.message : 'Failed to load diagnosis')
    } finally {
      setLoading(false)
    }
  }, [client, tenant])

  useEffect(() => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  if (!tenant || tenant === 'system') {
    return (
      <PageContainer>
        <PageHeading>Audit chain integrity</PageHeading>
        <MutedText>
          Please select a tenant to diagnose its audit chain.
        </MutedText>
      </PageContainer>
    )
  }

  if (error !== null) {
    return (
      <PageContainer>
        <PageHeading>Audit chain integrity</PageHeading>
        <Alert role="alert" $tone="danger">
          Failed to load the chain diagnosis: {error}
        </Alert>
      </PageContainer>
    )
  }

  if (loading || diagnosis === null) {
    return (
      <PageContainer>
        <PageHeading>Audit chain integrity</PageHeading>
        <MutedText role="status">Loading chain diagnosis…</MutedText>
      </PageContainer>
    )
  }

  return <AuditChainIntegrity diagnosis={diagnosis} />
}
